import {useTranslation} from "react-i18next";
import {useHistory, useLocation, useRouteMatch} from "react-router-dom";
import {css, keyframes, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import {borderRadius, calculateRemSize, flex, onDesktop, p, pt, pv} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text";

import {dataLayer} from "../../../../utils/data_layer";
import {useAppDispatch} from "../../../../utils/hooks/store_hooks";
import {chartsRoutes} from "../../common/app/routing/charts_routes";
import {getMenuItems} from "../../common/app/routing/menu_items";
import {breakpoints} from "../../common/app/styles/breakpoints";
import {OnboardingTrigger} from "../../features/analytical_platform/components/onboarding/OnboardingTrigger";
import {useEffectiveCitySlug} from "../../features/analytical_platform/hooks/use_effective_city_slug";
import {toggleOnboarding} from "../../features/analytical_platform/redux/onboarding_slice";
import {getSideMenuIcon} from "./side_menu_icons";

export const SideMenu = () => {
    const {path} = useRouteMatch();
    const citySlug = useEffectiveCitySlug();
    const history = useHistory();
    const {pathname} = useLocation();
    const {i18n} = useTranslation();
    const dispatch = useAppDispatch();

    const isLinkActive = (targetPath: string, i: number): boolean => {
        if (getHomepagePaths(i18n.language).includes(path) && i === 0) {
            return true;
        }
        return targetPath === pathname;
    };

    const handleMenuItemClick = (path: string, name: string) => {
        history.push(path);
        // setTimeout to prevent race condition between history.push and window.scrollTo
        setTimeout(() => {
            window.scrollTo(0, 0);
        }, 100);

        dataLayer({
            event: "left_menu_click",
            button: name,
            city: citySlug
        });
    };

    return (
        <div css={sideMenu}>
            <nav css={sideMenuNav}>
                {getMenuItems(citySlug, pathname, i18n).map(({name, path, secondPath, icon}, i) => (
                    <StyledNavLink
                        onClick={() => handleMenuItemClick(path, name)}
                        isActive={isLinkActive(path, i) || isLinkActive(secondPath, i)}
                        key={i}
                    >
                        {getSideMenuIcon(icon)}

                        <Text className="name-label" variant="body_copy_2">
                            {name}
                        </Text>
                    </StyledNavLink>
                ))}
            </nav>

            <OnboardingTrigger css={onboarding} onTriggerClick={() => dispatch(toggleOnboarding(true))} button />
        </div>
    );
};

const getHomepagePaths = (lang: string) => [
    chartsRoutes[lang].properties.root,
    chartsRoutes[lang].flats.root,
    chartsRoutes[lang].houses.root
];

const sideMenu = (theme: Theme) => css`
    display: none;

    @media (min-width: ${breakpoints.md}) {
        min-height: 100vh;
        background-color: ${theme.colors.secondary};
        ${pt(8)};
        display: block;
        position: relative;
        z-index: 99;
    }
`;

const sideMenuNav = css`
    ${onDesktop(css`
        position: sticky;
        top: ${calculateRemSize(8)};
        width: var(--sidebar-width);
    `)};
`;

export const showLabelAnimation = keyframes`
    from {
        left: 50%;
        opacity: 0;
    }
    to {
        left: 85%;
        opacity: 1;
    }
`;

export const labelName = css`
    background: #fff;
    ${p(0.5, 3)};
    ${borderRadius(0.5)};
    box-shadow: var(--box-shadow);
    font-weight: 600;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0;
    user-select: none;
    pointer-events: none;
    white-space: nowrap;
`;

const StyledNavLink = styled.div<{isActive?: boolean}>`
    ${pv(3)};
    text-align: center;
    transition:
        opacity 0.2s,
        left 0.2s,
        background 0.2s;
    position: relative;
    cursor: pointer;
    background: ${(props) => (props.isActive ? props.theme.colors.gray[100] : "none")};

    & > svg {
        transition:
            fill 0.2s,
            color 0.2s;
        fill: ${(props) => (props.isActive ? props.theme.colors.secondary : "#fff")};
        color: ${(props) => (props.isActive ? props.theme.colors.secondary : "#fff")};
    }

    &:hover {
        background: ${(props) => (props.isActive ? "#fff" : props.theme.colors.primary)};

        & > svg {
            fill: ${(props) => props.theme.colors.secondary};
            color: ${(props) => props.theme.colors.secondary};
        }

        .name-label {
            animation: ${showLabelAnimation} 0.35s ease 1;
            animation-fill-mode: forwards;
        }
    }

    .name-label {
        display: ${(props) => (props.isActive ? "none" : "block")};
        color: ${(props) => props.theme.colors.secondary};
        ${labelName};
    }
`;

const onboarding = (theme: Theme) => css`
    display: none;

    ${onDesktop(css`
        position: fixed;
        bottom: 0;
        ${pv(3)};
        width: var(--sidebar-width);
        transition:
            opacity 0.2s,
            left 0.2s,
            background 0.2s;
        ${flex("center", "center")};

        & > svg {
            transition: fill 0.2s;
        }

        &:hover {
            background: ${theme.colors.primary};

            & > svg {
                fill: ${theme.colors.secondary};
            }
        }
    `)};
`;
